/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "./content.css";
import { useNavigate } from "react-router";
import {
  validateContactJoinCmsBackup,
  normalizeContactFromBackup,
  normalizeJoinFromBackup,
} from "../../../utils/contactJoinCmsBundle";

const ContactJoin = () => {
  const [contact, setContact] = useState({
    mainText: "",
    arMainText: "",
    sectionTitle: "",
    arSectionTitle: "",
    subText: "",
    arSubText: "",
    description: "",
    arDescription: "",
  });

  const [join, setJoin] = useState({
    mainText: "",
    arMainText: "",
    sectionTitle: "",
    arSectionTitle: "",
    description: "",
    arDescription: "",
  });

  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [applyImportLoading, setApplyImportLoading] = useState(false);
  const [directImportOnUpload, setDirectImportOnUpload] = useState(false);
  const [pendingImportPayload, setPendingImportPayload] = useState(null);
  const importFileRef = useRef(null);

  const token =
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

  const navigate = useNavigate();

  const loadContent = useCallback(async () => {
    try {
      const response = await axios.get("/content");

      if (response.data.contact) {
        setContact(normalizeContactFromBackup(response.data.contact));
      }
      if (response.data.join) {
        setJoin(normalizeJoinFromBackup(response.data.join));
      }
    } catch (error) {
      toast.error("Failed to fetch data.!");
    }
  }, []);

  useEffect(() => {
    if (!token) {
      navigate("/dashboard/login");
    }
  }, [token, navigate]);

  useEffect(() => {
    if (token) loadContent();
  }, [token, loadContent]);

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContact((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleJoinChange = (e) => {
    const { name, value } = e.target;
    setJoin((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveData = async (e) => {
    e.preventDefault();

    const data = {
      contact,
      join,
    };

    try {
      await axios.post("/content", data, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Data saved successfully!");
    } catch (error) {
      toast.error("Failed to save data.!");
    }
  };

  const applyBundlePreviewToForm = useCallback((data) => {
    if ("contact" in data) {
      setContact(normalizeContactFromBackup(data.contact));
    }
    if ("join" in data) {
      setJoin(normalizeJoinFromBackup(data.join));
    }
  }, []);

  const buildImportApplyBody = useCallback(() => {
    if (!pendingImportPayload) return null;
    const p = pendingImportPayload;
    const body = {};
    if ("contact" in p) body.contact = contact;
    if ("join" in p) body.join = join;
    return Object.keys(body).length ? body : null;
  }, [pendingImportPayload, contact, join]);

  const handleDiscardStagedImport = async () => {
    setPendingImportPayload(null);
    await loadContent();
    toast.info("Preview discarded. Restored data from the server.");
  };

  const handleApplyImportedData = async () => {
    if (!token) {
      toast.error("Please sign in again.");
      return;
    }
    if (!pendingImportPayload) {
      toast.warn("Load a JSON file first.");
      return;
    }
    const body = buildImportApplyBody();
    if (!body || Object.keys(body).length === 0) {
      toast.error("Nothing to apply.");
      return;
    }
    try {
      setApplyImportLoading(true);
      await axios.post("/content/contact-join-cms/import", body, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setPendingImportPayload(null);
      toast.success("Imported data saved to the database.");
      await loadContent();
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Could not apply imported data."
      );
    } finally {
      setApplyImportLoading(false);
    }
  };

  const handleDownloadCmsBundle = async () => {
    if (!token) {
      toast.error("Please sign in again.");
      return;
    }
    try {
      setExportLoading(true);
      const res = await axios.get("/content/contact-join-cms/bundle", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = JSON.stringify(res.data, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contact-join-cms-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Contact & Join backup downloaded.");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Could not export Contact / Join CMS data."
      );
    } finally {
      setExportLoading(false);
    }
  };

  const handleImportFileSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!token) {
      toast.error("Please sign in again.");
      return;
    }
    const nameOk = /\.json$/i.test(file.name);
    const typeOk =
      file.type === "application/json" || file.type === "text/json" || !file.type;
    if (!nameOk && !typeOk) {
      toast.error("Please choose a .json file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        setImportLoading(true);
        let data;
        try {
          data = JSON.parse(String(reader.result || ""));
        } catch {
          toast.error("Invalid JSON. The file could not be parsed.");
          return;
        }
        const check = validateContactJoinCmsBackup(data);
        if (!check.ok) {
          toast.error(check.message);
          return;
        }

        const snapshot = JSON.parse(JSON.stringify(data));

        if (directImportOnUpload) {
          const body = {};
          if ("contact" in data) body.contact = normalizeContactFromBackup(data.contact);
          if ("join" in data) body.join = normalizeJoinFromBackup(data.join);
          await axios.post("/content/contact-join-cms/import", body, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
          setPendingImportPayload(null);
          toast.success("Contact / Join CMS data saved to the database.");
          await loadContent();
        } else {
          setPendingImportPayload(snapshot);
          applyBundlePreviewToForm(data);
          toast.success(
            "Import file loaded into the form. Review, then click Apply Imported Data."
          );
        }
      } catch (error) {
        console.error(error);
        toast.error(
          error.response?.data?.message || "Import failed. Check the file format."
        );
      } finally {
        setImportLoading(false);
      }
    };
    reader.onerror = () => {
      toast.error("Could not read the file.");
      setImportLoading(false);
    };
    reader.readAsText(file, "UTF-8");
  };

  return (
    <div className="dash-content home about">
      <div className="container">
        <form onSubmit={handleSaveData}>
          <div className="section one">
            <h1 className="dash-title">Contact</h1>
            <div className="input">
              <h2>Main Text</h2>
              <input
                type="text"
                name="mainText"
                placeholder="Main Text"
                value={contact.mainText}
                onChange={handleContactChange}
              />
              <h2 dir="rtl">النص الرئيسي</h2>
              <input
                dir="rtl"
                type="text"
                name="arMainText"
                placeholder="النص الرئيسي"
                value={contact.arMainText}
                onChange={handleContactChange}
              />
            </div>
            <div className="input">
              <h2>Section Title</h2>
              <input
                type="text"
                name="sectionTitle"
                placeholder="Section Title"
                value={contact.sectionTitle}
                onChange={handleContactChange}
              />
              <h2 dir="rtl">عنوان القسم</h2>
              <input
                dir="rtl"
                type="text"
                name="arSectionTitle"
                placeholder="عنوان القسم"
                value={contact.arSectionTitle}
                onChange={handleContactChange}
              />
            </div>
            <div className="input">
              <h2>Sub Text</h2>
              <input
                type="text"
                name="subText"
                placeholder="Sub Text"
                value={contact.subText}
                onChange={handleContactChange}
              />
              <h2 dir="rtl">النص الفرعي</h2>
              <input
                dir="rtl"
                type="text"
                name="arSubText"
                placeholder="النص الفرعي"
                value={contact.arSubText}
                onChange={handleContactChange}
              />
            </div>
            <div className="input">
              <h2>Description</h2>
              <input
                type="text"
                name="description"
                placeholder="Description"
                value={contact.description}
                onChange={handleContactChange}
              />
              <h2 dir="rtl">الوصف</h2>
              <input
                dir="rtl"
                type="text"
                name="arDescription"
                placeholder="الوصف"
                value={contact.arDescription}
                onChange={handleContactChange}
              />
            </div>
          </div>
          <hr />

          <div className="section one">
            <h1 className="dash-title">Join</h1>
            <div className="input">
              <h2>Main Text</h2>
              <input
                type="text"
                name="mainText"
                placeholder="Main Text"
                value={join.mainText}
                onChange={handleJoinChange}
              />
              <h2 dir="rtl">النص الرئيسي</h2>
              <input
                dir="rtl"
                type="text"
                name="arMainText"
                placeholder="النص الرئيسي"
                value={join.arMainText}
                onChange={handleJoinChange}
              />
            </div>
            <div className="input">
              <h2>Section Title</h2>
              <input
                type="text"
                name="sectionTitle"
                placeholder="Section Title"
                value={join.sectionTitle}
                onChange={handleJoinChange}
              />
              <h2 dir="rtl">عنوان القسم</h2>
              <input
                dir="rtl"
                type="text"
                name="arSectionTitle"
                placeholder="عنوان القسم"
                value={join.arSectionTitle}
                onChange={handleJoinChange}
              />
            </div>
            <div className="input">
              <h2>Description</h2>
              <input
                type="text"
                name="description"
                placeholder="Description"
                value={join.description}
                onChange={handleJoinChange}
              />
              <h2 dir="rtl">الوصف</h2>
              <input
                dir="rtl"
                type="text"
                name="arDescription"
                placeholder="الوصف"
                value={join.arDescription}
                onChange={handleJoinChange}
              />
            </div>
          </div>
          <button type="submit" className="submit">
            Save
          </button>
        </form>

        <section
          className="about-us-backup-panel"
          aria-label="Backup and restore Contact and Join page copy"
        >
          <div className="about-us-backup-panel__head">
            <div className="about-us-backup-panel__head-icon" aria-hidden="true">
              <i className="uil uil-database-alt" />
            </div>
            <div className="about-us-backup-panel__head-text">
              <h2 className="about-us-backup-panel__title">Backup &amp; import</h2>
              <p className="about-us-backup-panel__subtitle">
                Export or restore the <strong>Contact</strong> and <strong>Join</strong> headings
                and text shown on the site (English / Arabic). This is separate from the ZIP backup
                for job applications and contact form messages.
              </p>
            </div>
          </div>

          <ol className="about-us-backup-steps" aria-label="Import workflow">
            <li>
              <span className="about-us-backup-steps__num">1</span>
              <span>
                <strong>Download</strong> a JSON backup before risky changes.
              </span>
            </li>
            <li>
              <span className="about-us-backup-steps__num">2</span>
              <span>
                <strong>Load JSON</strong> — fills the form only (no database write).
              </span>
            </li>
            <li>
              <span className="about-us-backup-steps__num">3</span>
              <span>
                <strong>Apply imported data</strong> — saves to the server (overwrites included
                sections).
              </span>
            </li>
          </ol>

          <div className="about-us-backup-actions">
            <button
              type="button"
              className="about-us-backup-btn about-us-backup-btn--export"
              disabled={exportLoading || importLoading || applyImportLoading}
              onClick={handleDownloadCmsBundle}
            >
              <i className="uil uil-export" aria-hidden="true" />
              {exportLoading ? "Exporting…" : "Download JSON backup"}
            </button>
            <input
              ref={importFileRef}
              type="file"
              accept=".json,application/json,text/json"
              className="about-us-backup-file-input"
              onChange={handleImportFileSelected}
            />
            <button
              type="button"
              className="about-us-backup-btn about-us-backup-btn--import"
              disabled={exportLoading || importLoading || applyImportLoading}
              onClick={() => importFileRef.current?.click()}
            >
              <i className="uil uil-import" aria-hidden="true" />
              {importLoading
                ? "Reading file…"
                : directImportOnUpload
                  ? "Upload & save now"
                  : "Load JSON into form"}
            </button>
          </div>

          <div className="about-us-backup-advanced-row">
            <label className="about-us-backup-switch">
              <input
                type="checkbox"
                className="about-us-backup-switch__input"
                checked={directImportOnUpload}
                onChange={(e) => {
                  const on = e.target.checked;
                  setDirectImportOnUpload(on);
                  if (on) setPendingImportPayload(null);
                }}
                disabled={importLoading || applyImportLoading}
              />
              <span className="about-us-backup-switch__track" aria-hidden="true">
                <span className="about-us-backup-switch__thumb" />
              </span>
              <span className="about-us-backup-switch__label">
                Advanced: write to database immediately on upload (skip preview &amp; Apply)
              </span>
            </label>
          </div>

          {pendingImportPayload && !directImportOnUpload && (
            <div className="about-us-import-staged">
              <div className="about-us-import-staged__icon" aria-hidden="true">
                <i className="uil uil-exclamation-triangle" />
              </div>
              <div className="about-us-import-staged__body">
                <p className="about-us-import-warning" role="alert">
                  <strong>Preview loaded.</strong> Data is in the form only until you apply.{" "}
                  <strong className="about-us-import-warning__emphasis">
                    Applying will overwrite current server data
                  </strong>{" "}
                  for Contact and/or Join sections included in this file.
                </p>
                <div className="about-us-import-staged-actions">
                  <button
                    type="button"
                    className="about-us-backup-btn about-us-backup-btn--apply"
                    disabled={applyImportLoading}
                    onClick={handleApplyImportedData}
                  >
                    {applyImportLoading ? "Applying…" : "Apply imported data"}
                  </button>
                  <button
                    type="button"
                    className="about-us-backup-btn about-us-backup-btn--ghost"
                    disabled={applyImportLoading}
                    onClick={handleDiscardStagedImport}
                  >
                    Discard preview
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ContactJoin;
